import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ApiError } from '../../../api/client';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { useToastStore } from '../../../stores/toastStore';
import { useImprovementSignals } from '../../document-types/discovery/hooks';
import type { DocumentTypeResponse } from '../api';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DuplicateDialog } from '../components/DuplicateDialog';
import { StatusBadge } from '../components/StatusBadge';
import { useConnections } from '../hooks/useConnections';
import { useDocumentTypeMutations } from '../hooks/useDocumentTypeMutations';
import { useDocumentTypes } from '../hooks/useDocumentTypes';

interface DuplicateTarget {
  slug: string;
  name: string;
}

/**
 * Lists every published document type in the tenant plus their draft (when present).
 * Mounted at `/document-types`. Actions: editar, eliminar (con confirmación).
 *
 * Notas de scope:
 * - "Publicar" inline se ejerce desde el editor (`/:slug`), no aquí. La lista
 *   solo da navegación + delete + duplicate; los flujos transaccionales viven
 *   donde está toda la información del tipo.
 * - "Duplicar" (Wave 9.B) abre un modal con un slug pre-rellenado, llama al
 *   POST /document-types existente con los campos del origen y redirige al
 *   editor del nuevo borrador.
 * - "Última modificación" no la expone el backend (DocumentTypeResponse no tiene
 *   UpdatedAt); columna omitida con TODO en el endpoint.
 */
export function DocumentTypesListPage(): JSX.Element {
  const { data: types, isLoading, isError, error } = useDocumentTypes();
  const { data: providers } = useConnections();
  const mutations = useDocumentTypeMutations();
  const pushToast = useToastStore((s) => s.push);
  const navigate = useNavigate();

  const [slugToDelete, setSlugToDelete] = useState<string | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<DuplicateTarget | null>(null);

  const providerAlias = useMemo(() => {
    const map = new Map<string, string>();
    for (const provider of providers ?? []) {
      map.set(provider.providerId, provider.alias);
    }
    return map;
  }, [providers]);

  const handleConfirmDelete = (): void => {
    if (slugToDelete === null) {
      return;
    }
    const slug = slugToDelete;
    mutations.remove.mutate(slug, {
      onSuccess: () => {
        pushToast({ tone: 'success', title: `Tipo "${slug}" eliminado.` });
        setSlugToDelete(null);
      },
      onError: (err) => {
        const message =
          err instanceof ApiError
            ? err.message
            : 'No se pudo eliminar el tipo de documento.';
        pushToast({ tone: 'error', title: 'Error al eliminar', description: message });
        setSlugToDelete(null);
      },
    });
  };

  const handleConfirmDuplicate = (newSlug: string): void => {
    if (duplicateTarget === null) {
      return;
    }
    const sourceSlug = duplicateTarget.slug;
    mutations.duplicate.mutate(
      { sourceSlug, newSlug },
      {
        onSuccess: (created) => {
          pushToast({ tone: 'success', title: `Tipo duplicado como "${created.slug}"` });
          setDuplicateTarget(null);
          navigate(`/document-types/${encodeURIComponent(created.slug)}`);
        },
        onError: (err) => {
          // 422 from the API means the slug already exists (or fails the slug regex
          // on the server side — but client-side validation should catch the latter).
          // We treat both cases as recoverable: keep the dialog open so the user can
          // pick a different slug.
          if (err instanceof ApiError && err.status === 422) {
            pushToast({
              tone: 'error',
              title: 'Slug ya existe',
              description:
                err.message !== ''
                  ? err.message
                  : 'Ya hay un tipo de documento con ese slug en tu tenant.',
            });
            return;
          }
          const message =
            err instanceof ApiError ? err.message : 'No se pudo duplicar el tipo.';
          pushToast({ tone: 'error', title: 'Error al duplicar', description: message });
          setDuplicateTarget(null);
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Tipos de documento</h1>
          <p className="mt-1 text-sm text-slate-500">
            Define la estructura de los documentos que tu tenant procesa.
          </p>
        </div>
        {/*
          Phase 6 Wave 11.F — Discovery se promociona como flujo principal. La
          creación manual sigue accesible como acción secundaria para usuarios
          avanzados que ya tienen el schema en la cabeza.
        */}
        <div className="flex items-center gap-3">
          <Link to="/document-types/new" className="text-sm text-slate-500 hover:text-slate-700 hover:underline">
            Crear manualmente
          </Link>
          <Link to="/document-types/templates" className="btn-secondary">
            Usar plantilla por sector
          </Link>
          <Link to="/document-types/discover/upload" className="btn-primary">
            + Crear desde documentos
          </Link>
        </div>
      </header>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : isError ? (
        <ErrorBanner error={error} />
      ) : types === undefined || types.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Nombre</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Versión</th>
                <th className="px-4 py-3">Modelo IA</th>
                <th className="px-4 py-3">Señales</th>
                <th className="px-4 py-3">Actualizado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {types.map((type) => (
                <Row
                  key={`${type.id}-${type.version}`}
                  type={type}
                  providerAlias={providerAlias.get(type.modelProviderId)}
                  onDelete={() => setSlugToDelete(type.slug)}
                  onDuplicate={() =>
                    setDuplicateTarget({ slug: type.slug, name: type.name })
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={slugToDelete !== null}
        tone="danger"
        title={`¿Eliminar tipo "${slugToDelete ?? ''}"?`}
        description={
          <span>
            Esta acción borra el draft y todas las versiones publicadas. Los documentos
            procesados con este tipo se conservan: aparecerán en el listado etiquetados
            como <strong>«Tipo eliminado»</strong>. No podrás procesar nuevos documentos
            contra este slug hasta que vuelvas a crear el tipo.
          </span>
        }
        confirmLabel="Eliminar definitivamente"
        busy={mutations.remove.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setSlugToDelete(null)}
      />

      <DuplicateDialog
        open={duplicateTarget !== null}
        sourceSlug={duplicateTarget?.slug ?? ''}
        sourceName={duplicateTarget?.name ?? ''}
        busy={mutations.duplicate.isPending}
        onConfirm={handleConfirmDuplicate}
        onCancel={() => setDuplicateTarget(null)}
      />
    </div>
  );
}

interface RowProps {
  type: DocumentTypeResponse;
  providerAlias: string | undefined;
  onDelete: () => void;
  onDuplicate: () => void;
}

function Row({ type, providerAlias, onDelete, onDuplicate }: RowProps): JSX.Element {
  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-3 font-medium text-slate-900">{type.name}</td>
      <td className="px-4 py-3 font-mono text-xs text-slate-600">{type.slug}</td>
      <td className="px-4 py-3">
        <StatusBadge version={type.version} />
      </td>
      <td className="px-4 py-3 text-slate-700">
        {providerAlias ?? <span className="font-mono text-xs text-slate-400">{type.modelProviderId}</span>}
      </td>
      <td className="px-4 py-3">
        <SignalsBadge slug={type.slug} />
      </td>
      <td className="px-4 py-3 text-xs text-slate-600 tabular-nums">
        {formatUpdatedAt(type.updatedAt)}
      </td>
      <td className="px-4 py-3">
        <div className="flex justify-end gap-2">
          <Link to={`/document-types/${encodeURIComponent(type.slug)}`} className="btn-secondary">
            Editar
          </Link>
          <button type="button" onClick={onDuplicate} className="btn-secondary">
            Duplicar
          </button>
          <button type="button" onClick={onDelete} className="btn-secondary text-rose-700 hover:bg-rose-50">
            Eliminar
          </button>
        </div>
      </td>
    </tr>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="card p-10 text-center">
      <h2 className="text-base font-semibold text-slate-900">No hay tipos de documento</h2>
      <p className="mt-2 text-sm text-slate-500">
        Crea el primero para empezar a procesar documentos con tu schema.
      </p>
      <div className="mt-4 flex items-center justify-center gap-3">
        <Link to="/document-types/new" className="text-sm text-slate-500 hover:underline">
          Crear manualmente
        </Link>
        <Link to="/document-types/discover/upload" className="btn-primary inline-flex">
          + Crear desde documentos
        </Link>
      </div>
    </div>
  );
}

function ErrorBanner({ error }: { error: unknown }): JSX.Element {
  const message = error instanceof Error ? error.message : 'Error desconocido.';
  return (
    <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
      <strong className="font-semibold">No se pudieron cargar los tipos.</strong>
      <p className="mt-1">{message}</p>
    </div>
  );
}

/**
 * Lazy badge for the "Señales" column. Each row fires its own
 * `useImprovementSignals` query; React Query dedupes + caches per slug so
 * the list remains responsive. A 0-count badge is hidden completely so the
 * column stays visually quiet when nothing is wrong.
 */
function SignalsBadge({ slug }: { slug: string }): JSX.Element {
  const { data, isLoading, isError } = useImprovementSignals(slug);

  if (isLoading) {
    return <span className="text-xs text-slate-400">…</span>;
  }
  if (isError) {
    return <span className="text-xs text-slate-400">—</span>;
  }
  const count = (data ?? []).length;
  if (count === 0) {
    return <span className="text-xs text-slate-300">0</span>;
  }
  return (
    <Link
      to={`/document-types/${encodeURIComponent(slug)}/recommendations`}
      className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 hover:bg-amber-200"
    >
      {count} activa{count === 1 ? '' : 's'}
    </Link>
  );
}

const dateTimeFormatter = new Intl.DateTimeFormat('es-ES', {
  dateStyle: 'short',
  timeStyle: 'short',
});

function formatUpdatedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return dateTimeFormatter.format(date);
}
